/* ============================================================================
   Public project package — /pkg/<token>, no sign-in. The studio shares a set of
   drawings/documents with the client, a consultant or the contractor. Everyone
   can open and download; on a CLIENT package the recipient leaves revision
   comments that flow back to the studio.
   ========================================================================== */
import { useEffect, useState } from "react";
import { Icon } from "../components/Icons";
import { transmittalDoc, pdfBlobUrl } from "../lib/pdf";
import { downloadSheetFiles } from "../lib/downloadSheets";
import { fmtDate } from "../lib/format";

interface Sheet { code: string; title: string; discipline: string; revision?: string; fileUrl?: string; previewUrl?: string; fileName?: string; page?: number }
interface Note { id: string; code?: string; text: string; by: string; at: string; resolved?: boolean }
interface Pkg {
  studio: string; projectName: string; projectCode: string; location: string;
  ref: string; title: string; audience: "client" | "consultant" | "contractor";
  note?: string; recipient?: string; sharedAt: string; sheets: Sheet[]; notes: Note[];
  receipt?: { by: string; at: string } | null;
}

export default function ProjectPackagePage({ token }: { token: string }) {
  const [pkg, setPkg] = useState<Pkg | null>(null);
  const [err, setErr] = useState("");
  const [notes, setNotes] = useState<Note[]>([]);
  const [text, setText] = useState("");
  const [sheetRef, setSheetRef] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [receipt, setReceipt] = useState<{ by: string; at: string } | null>(null);
  const [ackBusy, setAckBusy] = useState(false);
  const [dl, setDl] = useState<string | null>(null);

  /* Every download goes through the splitter: a sheet living inside a combined
     upload comes out as its own single-page PDF, named by drawing number. */
  async function downloadOne(s: Sheet) {
    if (dl) return;
    try { setDl("Preparing…"); await downloadSheetFiles([s]); } finally { setDl(null); }
  }
  async function downloadAll() {
    if (dl || !pkg) return;
    try {
      await downloadSheetFiles(pkg.sheets.filter((s) => s.fileUrl), (done, total) => setDl(`Preparing — ${done}/${total}`));
    } finally { setDl(null); }
  }

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`/api/pkg/${token}`, { cache: "no-store" });
        if (!res.ok) { setErr(res.status === 404 ? "This package link is no longer valid." : "Couldn't load the package."); return; }
        const d = await res.json() as Pkg;
        setPkg(d); setNotes(d.notes ?? []); setReceipt(d.receipt ?? null);
      } catch { setErr("Couldn't reach the server."); }
    })();
  }, [token]);

  async function submit() {
    if (!text.trim() || busy) return;
    setBusy(true);
    try {
      const res = await fetch(`/api/pkg/${token}/note`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ text: text.trim(), code: sheetRef.trim() || undefined, by: name.trim() || pkg?.recipient }),
      });
      if (res.ok) { const { note } = await res.json() as { note: Note }; setNotes((n) => [...n, note]); setText(""); setSheetRef(""); setSent(true); setTimeout(() => setSent(false), 2500); }
    } finally { setBusy(false); }
  }

  async function downloadTransmittal() {
    if (!pkg) return;
    // The public payload is flattened; rebuild the minimal Project/Package the doc needs.
    const deliverables = pkg.sheets.map((s, i) => ({
      id: `s${i}`, code: s.code, title: s.title, discipline: s.discipline,
      phase: "construction", status: "issued", revision: s.revision,
    })) as any[];
    const project: any = { name: pkg.projectName, code: pkg.projectCode, location: pkg.location, deliverables };
    const projPkg: any = {
      ref: pkg.ref, title: pkg.title, audience: pkg.audience, recipientName: pkg.recipient,
      deliverableIds: deliverables.map((d) => d.id), note: pkg.note, sharedBy: pkg.studio, sharedAt: pkg.sharedAt,
    };
    const url = await pdfBlobUrl(transmittalDoc(project, projPkg));
    const a = document.createElement("a");
    a.href = url; a.download = `${(pkg.projectCode || pkg.projectName).replace(/[^\w]+/g, "_")}_${pkg.ref}_transmittal.pdf`; a.click();
    setTimeout(() => URL.revokeObjectURL(url), 4000);
  }

  async function acknowledge() {
    if (ackBusy || receipt) return;
    setAckBusy(true);
    try {
      const res = await fetch(`/api/pkg/${token}/receipt`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ by: name.trim() || pkg?.recipient }),
      });
      if (res.ok) { const { receipt: r } = await res.json() as { receipt: { by: string; at: string } }; setReceipt(r); }
    } finally { setAckBusy(false); }
  }

  if (err) return <Centered><p style={{ fontSize: 15 }}>{err}</p></Centered>;
  if (!pkg) return <Centered><span className="dim">Loading…</span></Centered>;

  const isClient = pkg.audience === "client";
  const byDisc = new Map<string, Sheet[]>();
  for (const s of pkg.sheets) { const k = s.discipline || "Drawings"; if (!byDisc.has(k)) byDisc.set(k, []); byDisc.get(k)!.push(s); }

  return (
    <div style={{ minHeight: "100vh", background: "var(--paper)", color: "var(--ink)" }}>
      <div style={{ maxWidth: 860, margin: "0 auto", padding: "44px clamp(18px,4vw,44px) 90px" }}>
        <div style={{ borderBottom: "1px solid var(--line)", paddingBottom: 20, marginBottom: 24 }}>
          <div className="kicker" style={{ marginBottom: 8 }}>{pkg.studio} · {pkg.projectCode}</div>
          <h1 className="serif" style={{ fontSize: 34, lineHeight: 1.05 }}>{pkg.title}</h1>
          <div className="row wrap" style={{ gap: 8, marginTop: 12, alignItems: "center" }}>
            <span className="mono dim" style={{ fontSize: 12 }}>{pkg.ref}</span>
            <span className="badge" style={{ fontSize: 10, textTransform: "capitalize" }}>{pkg.audience} package</span>
            <span className="dim" style={{ fontSize: 12.5 }}>{pkg.projectName} · {pkg.location}</span>
            <button className="btn ghost sm" title="Download transmittal record (PDF)" onClick={() => void downloadTransmittal()}><Icon.download width={12} />Transmittal</button>
          </div>
          {pkg.note && <p className="muted" style={{ fontSize: 13.5, lineHeight: 1.6, marginTop: 14 }}>{pkg.note}</p>}
        </div>

        <div className="row between wrap" style={{ gap: 10, marginBottom: 12 }}>
          <span className="kicker">{pkg.sheets.length} sheet{pkg.sheets.length === 1 ? "" : "s"}</span>
          {pkg.sheets.some((s) => s.fileUrl) && (
            <button className="btn accent sm" disabled={!!dl} onClick={() => void downloadAll()}>
              <Icon.download width={13} />{dl ?? `Download all sheets (${pkg.sheets.filter((s) => s.fileUrl).length})`}
            </button>
          )}
        </div>
        <div className="col" style={{ gap: 18 }}>
          {[...byDisc.entries()].map(([disc, sheets]) => (
            <div key={disc}>
              <div className="kicker" style={{ fontSize: 9, color: "var(--accent-ink)", marginBottom: 8 }}>{disc}</div>
              <div className="col" style={{ gap: 8 }}>
                {sheets.map((s, i) => {
                  const href = s.fileUrl || s.previewUrl;
                  return (
                    <div key={i} className="card" style={{ padding: "12px 16px", display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
                      <span className="mono" style={{ fontSize: 12.5, fontWeight: 600, width: 84, flexShrink: 0 }}>{s.code}</span>
                      <span style={{ flex: 1, fontSize: 13.5, minWidth: 160 }}>{s.title}{s.revision ? <span className="dim mono" style={{ fontSize: 11, marginLeft: 8 }}>Rev {s.revision}</span> : null}</span>
                      {href ? (
                        <span className="row" style={{ gap: 6 }}>
                          <a className="btn ghost sm" target="_blank" rel="noreferrer" href={href}><Icon.arrowUpRight width={13} />Open</a>
                          {/* a sheet inside a combined upload downloads as ITS OWN
                              page, split out — never the whole package */}
                          <button className="btn accent sm" disabled={!!dl} onClick={() => void downloadOne(s)}><Icon.download width={13} />Download</button>
                        </span>
                      ) : <span className="dim" style={{ fontSize: 11.5 }}>No file</span>}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
          {pkg.sheets.length === 0 && <p className="dim">No sheets in this package.</p>}
        </div>

        <div className="card" style={{ marginTop: 26, padding: "16px 18px", display: "flex", gap: 14, alignItems: "center", flexWrap: "wrap", justifyContent: "space-between", background: receipt ? "var(--ok-bg, rgba(46,125,50,0.08))" : "var(--card)" }}>
          {receipt ? (
            <div className="row" style={{ gap: 10, alignItems: "center" }}>
              <Icon.check width={16} />
              <span style={{ fontSize: 13.5 }}>Receipt confirmed by <b>{receipt.by}</b> on {fmtDate(receipt.at)}. The studio has been notified.</span>
            </div>
          ) : (
            <>
              <div style={{ minWidth: 200, flex: 1 }}>
                <div style={{ fontSize: 13.5, fontWeight: 500 }}>Confirm receipt of this package</div>
                <div className="dim" style={{ fontSize: 12, marginTop: 2 }}>Acknowledging records who received {pkg.ref} and when — this closes the transmittal.</div>
              </div>
              <div className="row" style={{ gap: 8, alignItems: "center" }}>
                <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name" style={{ padding: "8px 11px", borderRadius: 8, border: "1px solid var(--line)", background: "var(--paper)", color: "var(--ink)", fontSize: 13, width: 150 }} />
                <button className="btn accent" disabled={ackBusy} onClick={() => void acknowledge()}><Icon.check width={13} />{ackBusy ? "Confirming…" : "Confirm receipt"}</button>
              </div>
            </>
          )}
        </div>

        {isClient && (
          <div style={{ marginTop: 34, borderTop: "1px solid var(--line)", paddingTop: 24 }}>
            <h2 className="serif" style={{ fontSize: 24, marginBottom: 6 }}>Your review comments</h2>
            <p className="dim" style={{ fontSize: 13, marginBottom: 16 }}>Note any revisions you'd like — they go straight to the studio.</p>
            <div className="col" style={{ gap: 10, marginBottom: 18 }}>
              {notes.map((n) => (
                <div key={n.id} className="panel" style={{ padding: "10px 13px" }}>
                  {n.code && <span className="badge mono" style={{ fontSize: 9.5, marginRight: 6 }}>{n.code}</span>}
                  <span style={{ fontSize: 13 }}>{n.text}</span>
                  <div className="dim" style={{ fontSize: 10.5, marginTop: 4 }}>{n.by} · {new Date(n.at).toLocaleString()}{n.resolved ? " · addressed by the studio" : ""}</div>
                </div>
              ))}
              {notes.length === 0 && <p className="dim" style={{ fontSize: 12.5 }}>No comments yet.</p>}
            </div>
            <div className="card" style={{ padding: 16 }}>
              <div className="row" style={{ gap: 10, marginBottom: 10, flexWrap: "wrap" }}>
                <input className="input" style={{ flex: 1, minWidth: 160 }} placeholder="Your name" value={name} onChange={(e) => setName(e.target.value)} />
                <input className="input" style={{ width: 150 }} placeholder="Sheet (optional)" value={sheetRef} onChange={(e) => setSheetRef(e.target.value)} />
              </div>
              <textarea className="input" style={{ minHeight: 80, width: "100%" }} placeholder="Describe the revision you'd like…" value={text} onChange={(e) => setText(e.target.value)} />
              <div className="row between" style={{ marginTop: 10 }}>
                {sent ? <span style={{ fontSize: 12.5, color: "var(--accent-ink)" }}><Icon.check width={13} /> Sent to the studio</span> : <span />}
                <button className="btn accent" onClick={() => void submit()} disabled={!text.trim() || busy}><Icon.send width={14} />{busy ? "Sending…" : "Send comment"}</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Centered({ children }: { children: React.ReactNode }) {
  return <div style={{ minHeight: "100vh", display: "grid", placeItems: "center", background: "var(--paper)", color: "var(--ink)", padding: 24, textAlign: "center" }}>{children}</div>;
}
